// Runtime polyfills for the repo-wide build target: chrome69 / safari15.
//
// `build.target` only downlevels syntax — it does not polyfill missing APIs.
// This module covers every unguarded usage found in the built bundles
// (paraglide runtime, ky, posthog, Solid core, motion, and app code).
// Import it as the FIRST module of every app entry so it runs before any
// dependency's module-init code (e.g. paraglide's `globalThis.__paraglide`).
/* eslint-disable @typescript-eslint/no-explicit-any */

// Chrome 71 / Safari 12.1 — paraglide runtime, ky, TanStack Query
if (typeof globalThis === 'undefined') {
  ;(window as any).globalThis = window
}

// Chrome 71 / Safari 12.1 — Solid core, motion
if (typeof queueMicrotask === 'undefined') {
  ;(globalThis as any).queueMicrotask = (cb: () => void) =>
    Promise.resolve()
      .then(cb)
      .catch((e) =>
        setTimeout(() => {
          throw e
        }),
      )
}

// Chrome 73 / Safari 12.1 — ky, posthog, paraglide messages
if (!Object.fromEntries) {
  Object.fromEntries = function fromEntries(
    entries: Iterable<readonly [PropertyKey, unknown]>,
  ) {
    const obj: Record<string, unknown> = {}
    for (const [k, v] of entries) obj[String(k)] = v
    return obj
  } as typeof Object.fromEntries
}

// Chrome 76 / Safari 13
if (!Promise.allSettled) {
  Promise.allSettled = ((promises: Iterable<unknown>) =>
    Promise.all(
      Array.from(promises, (p) =>
        Promise.resolve(p).then(
          (value) => ({ status: 'fulfilled' as const, value }),
          (reason: unknown) => ({ status: 'rejected' as const, reason }),
        ),
      ),
    )) as typeof Promise.allSettled
}

// Chrome 86 / Safari 14 — a ParentNode method, so DocumentFragment needs it
// too: a ShadowRoot is one (focus-pass mounts its main visual SVG into one)
for (const { prototype } of [Element, DocumentFragment]) {
  if (!prototype.replaceChildren) {
    prototype.replaceChildren = function replaceChildren(
      this: ParentNode & Node,
      ...nodes: (Node | string)[]
    ) {
      while (this.lastChild) this.removeChild(this.lastChild)
      if (nodes.length > 0) this.append(...nodes)
    }
  }
}

// Chrome 92 / Safari 15.4 — ky's non-AbortSignal.any fallback path
if (!Array.prototype.at) {
  // eslint-disable-next-line no-extend-native
  Array.prototype.at = function at(this: unknown[], n: number) {
    const i = Math.trunc(n) || 0
    const idx = i < 0 ? i + this.length : i
    return idx < 0 || idx >= this.length ? undefined : this[idx]
  }
}

// Chrome 93 / Safari 15.4 — ky option/header merging
if (!Object.hasOwn) {
  Object.hasOwn = (obj: object, prop: PropertyKey) =>
    Object.prototype.hasOwnProperty.call(obj, prop)
}

// Chrome 98 / Safari 15.4 — TanStack Query structural sharing
if (typeof structuredClone === 'undefined') {
  ;(globalThis as any).structuredClone = (val: unknown) =>
    JSON.parse(JSON.stringify(val))
}

// Chrome 100 / Safari 15.4 — ky retry delay
if (
  typeof AbortSignal !== 'undefined' &&
  !AbortSignal.prototype.throwIfAborted
) {
  AbortSignal.prototype.throwIfAborted = function throwIfAborted(
    this: AbortSignal,
  ) {
    if (this.aborted) {
      throw (
        (this as any).reason ??
        new DOMException('signal is aborted without reason', 'AbortError')
      )
    }
  }
}

export {}
